(() => {
  if (customElements.get('taffstyle-cart-recommendations')) return;
  const cache = new Map();
  let mutationPending = false;
  const rootUrl = () => window.Shopify?.routes?.root || '/';
  const request = (url, options = {}) => fetch(url, {credentials:'same-origin', ...options, signal:AbortSignal.timeout(12000)});
  const node = (tag, className, text) => {
    const el = document.createElement(tag);
    el.className = className;
    if (text !== undefined) el.textContent = text;
    return el;
  };
  class CartRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.products = [];
      this.selectedProduct = null;
      this.selections = new Map();
      try { this.data = JSON.parse(this.querySelector('[data-ts-rec-data]').textContent); }
      catch { return; }
      this.build();
      this.lifecycle = new AbortController();
      document.addEventListener('pointerdown', event => {
        if (!this.contains(event.target)) this.closePicker();
        else if (this.optionMenu && !this.optionMenu.contains(event.target) && !this.optionTrigger?.contains(event.target)) this.closeOptions();
      }, {signal:this.lifecycle.signal});
      document.addEventListener('keydown', event => {
        if (event.key !== 'Escape') return;
        if (this.optionMenu) { event.preventDefault(); this.closeOptions(true); }
        else if (!this.picker.hidden) { event.preventDefault(); this.closePicker(true); }
      }, {signal:this.lifecycle.signal});
      document.addEventListener('scroll', event => {
        if (this.optionMenu?.contains(event.target) || this.picker.contains(event.target)) return;
        if (this.selectedProduct) this.closePicker();
      }, {capture:true,signal:this.lifecycle.signal});
      document.addEventListener('focusin', event => {
        if (!this.contains(event.target)) this.closePicker();
      }, {signal:this.lifecycle.signal});
      window.addEventListener('resize', () => { this.closeOptions(); this.positionPicker(); }, {signal:this.lifecycle.signal});
      this.addEventListener('click', event => {
        this.bindDOM();
        const button = event.target.closest('button');
        if (!button || !this.contains(button) || button.disabled) return;
        if (button.dataset.recValue !== undefined) {
          event.preventDefault(); event.stopImmediatePropagation();
          this.selectOption(Number(button.dataset.recIndex),button.dataset.recValue);
        } else if (button.dataset.recIndex !== undefined) {
          event.preventDefault(); event.stopImmediatePropagation();
          this.openOptions(this.selectedProduct,Number(button.dataset.recIndex),button);
        } else if (button.dataset.recSelectedAdd) {
          event.preventDefault(); event.stopImmediatePropagation();
          const product = this.products.find(p => String(p.id) === button.dataset.recSelectedAdd);
          const variant = product && this.selectedVariant(product);
          if (variant) { this.closePicker(); this.add(variant); }
        } else if (button.dataset.recProduct) {
          event.preventDefault(); event.stopImmediatePropagation();
          const product = this.products.find(p => String(p.id) === button.dataset.recProduct);
          if (product) this.choose(product);
        } else if (button.dataset.recAction) {
          event.preventDefault(); event.stopImmediatePropagation();
          if (button.dataset.recAction === 'prev') this.move(-1);
          if (button.dataset.recAction === 'next') this.move(1);
          if (button.dataset.recAction === 'close') this.cancelSelection();
          if (button.dataset.recAction === 'add' && this.selectedProduct) {
            const variant = this.selectedVariant(this.selectedProduct);
            if (variant) { this.closePicker(); this.add(variant); }
          }
        }
      },true);
      this.addEventListener('scroll',event => { this.bindDOM(); this.navigation(); if (event.target === this.track) this.closePicker(); },true);
      const seeds = [...new Set(this.data.seeds.map(String))].slice(0, 2);
      this.render(this.data.fallback || []);
      Promise.all(seeds.map(id => {
        if (!cache.has(id)) cache.set(id, request(rootUrl() + 'recommendations/products.json?product_id=' + encodeURIComponent(id) + '&limit=10&intent=related').then(r => r.ok ? r.json() : {products:[]}).then(d => d.products || []).catch(() => []));
        return cache.get(id);
      })).then(groups => {
        if (this.isConnected && !this.selectedProduct) this.render([...groups.flat(), ...(this.data.fallback || [])]);
      });
    }
    disconnectedCallback() { this.resizeObserver?.disconnect(); this.closePicker(); this.lifecycle?.abort(); }
    money(amount) { return new Intl.NumberFormat(document.documentElement.lang || 'de', {style:'currency',currency:this.dataset.currency || 'EUR'}).format(amount / 100); }
    button(label, text, action, className = '') {
      const button = node('button',className,text);
      button.type = 'button';
      button.setAttribute('aria-label',label);
      button.addEventListener('click',action);
      return button;
    }
    build() {
      const head = node('div','ts-rec-head');
      const title = node('h3','',this.dataset.title);
      const nav = node('div','ts-rec-nav');
      this.prev = this.button(this.dataset.prev,'‹',() => this.move(-1));
      this.next = this.button(this.dataset.next,'›',() => this.move(1));
      this.prev.dataset.recAction = 'prev'; this.next.dataset.recAction = 'next';
      nav.append(this.prev,this.next);
      head.append(title,nav);
      this.track = node('div','ts-rec-track');
      this.track.setAttribute('role','list');
      this.track.setAttribute('aria-label',this.dataset.title);
      this.picker = node('div','ts-rec-picker');
      this.picker.id = 'ts-rec-picker-' + Math.random().toString(36).slice(2);
      this.picker.setAttribute('popover','manual');
      this.picker.setAttribute('role','group');
      this.picker.hidden = true;
      this.status = node('p','ts-rec-status');
      this.status.setAttribute('role','status');
      this.status.setAttribute('aria-live','polite');
      this.append(head,this.track,this.picker,this.status);
      this.track.addEventListener('scroll',() => this.navigation(),{passive:true});
      this.resizeObserver = new ResizeObserver(() => { this.navigation(); this.positionPicker(); });
      this.resizeObserver.observe(this.track);
    }
    available(product) {
      return (product.variants || []).filter(v => v.available && v.requires_shipping !== false && (v.quantity_rule?.min || 1) <= 1 && (v.quantity_rule?.increment || 1) <= 1);
    }
    render(products) {
      const inCart = new Set(this.data.seeds.map(String));
      const seen = new Set(inCart);
      this.products = products.filter(p => {
        if (seen.has(String(p.id)) || !p.available || p.requires_selling_plan || !p.featured_image || !this.available(p).length) return false;
        seen.add(String(p.id));
        return true;
      }).slice(0,6);
      this.closePicker();
      this.track.replaceChildren();
      for (const product of this.products) {
        const card = node('article','ts-rec-card');
        card.setAttribute('role','listitem');
        card.dataset.product = String(product.id);
        const top = node('div','ts-rec-product');
        const image = node('img','ts-rec-image');
        const imageUrl = new URL(typeof product.featured_image === 'string' ? product.featured_image : product.featured_image.src,location.origin);
        imageUrl.searchParams.set('width','160');
        image.src = imageUrl.href;
        image.alt = product.title;
        image.width = 44; image.height = 50; image.loading = 'lazy'; image.decoding = 'async';
        const name = node('p','ts-rec-name',product.title);
        name.title = product.title;
        top.append(image,name);
        const bottom = node('div','ts-rec-bottom');
        const variants = this.available(product);
        const min = Math.min(...variants.map(v => v.price));
        const max = Math.max(...variants.map(v => v.price));
        const specs = this.optionSpecs(product).filter(o => o.values.length > 1);
        const needsChoice = specs.length > 0;
        const actionLabel = needsChoice ? this.choiceLabel(specs) + ' ⌄' : this.dataset.addLabel;
        const optionNames = specs.map(o => o.name);
        const hint = node('span','ts-rec-option-hint',needsChoice ? (optionNames.join(' / ') || this.dataset.optionsLabel) : this.dataset.singleLabel);
        bottom.append(node('strong','ts-rec-price',min === max ? this.money(min) : this.money(min) + ' – ' + this.money(max)),this.button(actionLabel + ': ' + product.title,actionLabel,() => this.choose(product),'ts-rec-plus'));
        bottom.querySelector('button').dataset.recProduct = String(product.id);
        if (needsChoice) {
          bottom.querySelector('button').setAttribute('aria-expanded','false');
        }
        card.append(top,hint,bottom);
        this.track.append(card);
        if (this.selections.has(String(product.id))) this.updateCard(product);
      }
      this.hidden = !this.products.length;
      this.navigation();
    }
    bindDOM() {
      this.track = this.querySelector('.ts-rec-track');
      this.picker = this.querySelector('.ts-rec-picker');
      this.status = this.querySelector('.ts-rec-status');
      this.prev = this.querySelector('[data-rec-action="prev"]');
      this.next = this.querySelector('[data-rec-action="next"]');
    }
    navigation() {
      this.bindDOM();
      if (!this.track || !this.prev || !this.next) return;
      this.prev.disabled = this.track.scrollLeft <= 2;
      this.next.disabled = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;
    }
    move(direction) {
      this.bindDOM();
      const card = this.track.firstElementChild;
      if (card) {
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        this.track.scrollBy({left:direction * 2 * (card.getBoundingClientRect().width + gap),behavior:matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
      }
    }
    optionSpecs(product) {
      return (product.options || []).map((option,index) => ({
        index,
        name:typeof option === 'string' ? option : option.name,
        values:[...new Set((product.variants || []).map(v => this.optionValue(v,index)).filter(Boolean))]
      })).filter(option => option.values.length);
    }
    optionValue(variant,index) { return String(variant.options?.[index] ?? variant['option' + (index + 1)] ?? ''); }
    state(product) {
      const key = String(product.id);
      if (!this.selections.has(key)) this.selections.set(key,this.optionSpecs(product).map(o => o.values.length === 1 ? o.values[0] : null));
      return this.selections.get(key);
    }
    selectedVariant(product) {
      const state = this.state(product);
      const specs = this.optionSpecs(product);
      if (specs.some(o => !state[o.index])) return null;
      return this.available(product).find(v => specs.every(o => this.optionValue(v,o.index) === state[o.index])) || null;
    }
    valueAvailable(product,index,value) {
      const state = this.state(product);
      return this.available(product).some(v => this.optionValue(v,index) === value &&
        (index === this.colorIndex(product) || this.optionSpecs(product).every(o =>
          o.index === index || !state[o.index] || this.optionValue(v,o.index) === state[o.index])));
    }
    choiceLabel(specs) {
      if (specs.length !== 1) return this.dataset.variantLabel;
      if (/^(farbe|colou?r)$/i.test(specs[0].name)) return this.dataset.colorLabel;
      if (/größe|groesse|size|durchmesser|diameter/i.test(specs[0].name)) return this.dataset.sizeLabel;
      return specs[0].name;
    }
    choose(product) {
      this.bindDOM();
      if (mutationPending) return;
      const specs = this.optionSpecs(product).filter(o => o.values.length > 1);
      if (!specs.length) { const variant = this.available(product)[0]; if (variant) this.add(variant); return; }
      const trigger = this.querySelector('[data-rec-product="' + product.id + '"]');
      if (this.pickerTrigger === trigger && (!this.picker.hidden || this.optionMenu)) { this.closePicker(true); return; }
      this.closePicker();
      this.selectedProduct = product;
      this.pickerTrigger = trigger;
      this.status.textContent = '';
      this.picker.replaceChildren();
      if (specs.length === 1) { this.openOptions(product,specs[0].index,trigger); return; }
      this.picker.setAttribute('aria-label',product.title);
      const fields = node('div','ts-rec-fields');
      for (const spec of specs) {
        const button = this.button(spec.name,'',() => {},'ts-rec-field');
        button.dataset.recIndex = String(spec.index);
        button.setAttribute('aria-haspopup','listbox');
        button.setAttribute('aria-expanded','false');
        fields.append(button);
      }
      const close = this.button(this.dataset.cancelLabel,'×',() => {},'ts-rec-cancel');
      close.dataset.recAction = 'close';
      const add = this.button(this.dataset.addLabel,this.dataset.addLabel,() => {},'ts-rec-add');
      add.dataset.recAction = 'add';
      this.picker.append(close,fields,add);
      this.updatePicker();
      this.picker.hidden = false;
      trigger.setAttribute('aria-expanded','true');
      trigger.setAttribute('aria-controls',this.picker.id);
      if (this.picker.showPopover) this.picker.showPopover();
      this.positionPicker();
      fields.querySelector('button')?.focus({preventScroll:true});
    }
    positionPicker() {
      if (!this.picker || this.picker.hidden || !this.pickerTrigger?.isConnected) return;
      const rect = this.pickerTrigger.closest('.ts-rec-card').getBoundingClientRect();
      const width = Math.min(286,innerWidth - 24,Math.max(220,rect.right - 12));
      this.picker.style.width = width + 'px';
      this.picker.style.left = Math.max(12,Math.min(rect.right - width,innerWidth - width - 12)) + 'px';
      const height = this.picker.getBoundingClientRect().height;
      const above = rect.top - height - 5;
      const below = rect.bottom + 5;
      this.picker.style.top = Math.max(8,below + height > innerHeight - 12 && above >= 8 ? above : Math.min(below,innerHeight - height - 12)) + 'px';
    }
    updatePicker() {
      const product = this.selectedProduct;
      if (!product) return;
      const state = this.state(product);
      for (const button of this.picker.querySelectorAll('[data-rec-index]')) {
        const index = Number(button.dataset.recIndex);
        const spec = this.optionSpecs(product).find(o => o.index === index);
        const label = node('span','ts-rec-field-value',state[index] || this.choiceLabel([spec]));
        if (state[index] && index === this.colorIndex(product)) {
          const variant = (product.variants || []).find(v => this.optionValue(v,index) === state[index]);
          label.prepend(this.swatch(product,variant));
        }
        button.replaceChildren(label,node('span','ts-rec-chevron','⌄'));
        button.setAttribute('aria-label',spec.name + ': ' + (state[index] || this.choiceLabel([spec])));
      }
      const add = this.picker.querySelector('[data-rec-action="add"]');
      if (add) add.disabled = !this.selectedVariant(product);
    }
    updateCard(product) {
      const card = this.querySelector('[data-product="' + product.id + '"]');
      if (!card) return;
      const specs = this.optionSpecs(product).filter(o => o.values.length > 1);
      const state = this.state(product);
      const variant = this.selectedVariant(product);
      const trigger = card.querySelector('[data-rec-product]');
      const choices = specs.map(o => state[o.index]).filter(Boolean);
      card.querySelector('.ts-rec-option-hint').textContent = choices.length ? choices.join(' · ') : specs.map(o => o.name).join(' / ');
      card.classList.toggle('ts-rec-card-selected',choices.length > 0);
      trigger.textContent = (variant ? (specs.length > 1 ? this.dataset.changeLabel : state[specs[0].index]) : this.choiceLabel(specs)) + ' ⌄';
      const prices = this.available(product).map(v => v.price);
      const min = Math.min(...prices), max = Math.max(...prices);
      card.querySelector('.ts-rec-price').textContent = variant ? this.money(variant.price) : min === max ? this.money(min) : this.money(min) + ' – ' + this.money(max);
      const source = variant && this.variantImage(variant) || (typeof product.featured_image === 'string' ? product.featured_image : product.featured_image.src);
      const url = new URL(source,location.origin); url.searchParams.set('width','160');
      card.querySelector('.ts-rec-image').src = url.href;
      card.querySelector('.ts-rec-image').alt = product.title + (variant ? ' · ' + variant.title : '');
      card.querySelector('[data-rec-selected-add]')?.remove();
      if (variant && specs.length === 1) {
        const add = this.button(this.dataset.addLabel + ': ' + product.title,this.dataset.addLabel,() => {},'ts-rec-plus ts-rec-selected-add');
        add.dataset.recSelectedAdd = String(product.id);
        card.append(add);
      }
    }
    selectOption(index,value) {
      const product = this.selectedProduct;
      if (!product || !this.valueAvailable(product,index,value)) return;
      const state = this.state(product);
      state[index] = value;
      const specs = this.optionSpecs(product);
      if (!this.available(product).some(v => specs.every(o => !state[o.index] || this.optionValue(v,o.index) === state[o.index]))) {
        for (const spec of specs) if (spec.index !== index && spec.values.length > 1) state[spec.index] = null;
      }
      this.closeOptions(true);
      this.updatePicker();
      this.updateCard(product);
      this.positionPicker();
    }
    closeOptions(focus = false) {
      const trigger = this.optionTrigger;
      if (this.optionMenu) {
        if (this.optionMenu.hidePopover && this.optionMenu.matches(':popover-open')) this.optionMenu.hidePopover();
        this.optionMenu.remove();
      }
      trigger?.setAttribute('aria-expanded','false');
      trigger?.removeAttribute('aria-controls');
      this.optionMenu = null;
      this.optionTrigger = null;
      if (focus && trigger?.isConnected) trigger.focus({preventScroll:true});
    }
    closePicker(focus = false) {
      this.closeOptions();
      const trigger = this.pickerTrigger;
      if (this.picker) {
        if (this.picker.hidePopover && this.picker.matches(':popover-open')) this.picker.hidePopover();
        this.picker.hidden = true;
      }
      trigger?.setAttribute('aria-expanded','false');
      trigger?.removeAttribute('aria-controls');
      this.pickerTrigger = null;
      this.selectedProduct = null;
      if (focus && trigger?.isConnected) trigger.focus({preventScroll:true});
    }
    cancelSelection() {
      const product = this.selectedProduct;
      if (product) { this.selections.delete(String(product.id)); this.updateCard(product); }
      this.closePicker(true);
    }
    openOptions(product,index,trigger) {
      if (!product) return;
      if (this.optionTrigger === trigger) { this.closeOptions(true); return; }
      this.closeOptions();
      const spec = this.optionSpecs(product).find(o => o.index === index);
      const menu = node('div','ts-rec-colors');
      menu.id = 'ts-rec-options-' + Math.random().toString(36).slice(2);
      menu.setAttribute('role','listbox');
      menu.setAttribute('aria-label',spec.name);
      menu.setAttribute('popover','manual');
      const single = this.optionSpecs(product).filter(o => o.values.length > 1).length === 1;
      for (const value of spec.values) {
        const variants = this.available(product).filter(v => this.optionValue(v,index) === value);
        const option = this.button(value,'',() => {},'ts-rec-color-option');
        option.dataset.recIndex = String(index); option.dataset.recValue = value;
        option.setAttribute('role','option');
        option.setAttribute('aria-selected',String(this.state(product)[index] === value));
        option.disabled = !this.valueAvailable(product,index,value);
        option.setAttribute('aria-disabled',String(option.disabled));
        if (index === this.colorIndex(product)) option.append(this.swatch(product,(product.variants || []).find(v => this.optionValue(v,index) === value)));
        else option.classList.add('ts-rec-text-option');
        option.append(node('span','',value + (single && variants.length ? ' · ' + this.money(Math.min(...variants.map(v => v.price))) : '')));
        menu.append(option);
      }
      menu.addEventListener('keydown',event => {
        const options = [...menu.querySelectorAll('button:not(:disabled)')];
        const current = options.indexOf(document.activeElement);
        let next;
        if (event.key === 'ArrowDown') next = (current + 1) % options.length;
        if (event.key === 'ArrowUp') next = (current - 1 + options.length) % options.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = options.length - 1;
        if (next !== undefined && options.length) { event.preventDefault(); options[next].focus(); }
        if (event.key === 'Tab') this.closeOptions(true);
      });
      this.append(menu);
      this.optionMenu = menu; this.optionTrigger = trigger;
      trigger.setAttribute('aria-expanded','true'); trigger.setAttribute('aria-controls',menu.id);
      const rect = trigger.getBoundingClientRect();
      const width = Math.min(Math.max(rect.width,single ? 220 : 190),innerWidth - 24);
      menu.style.width = width + 'px';
      if (menu.showPopover) menu.showPopover();
      const below = innerHeight - rect.bottom - 12, above = rect.top - 12;
      const upward = below < Math.min(190,menu.scrollHeight) && above > below;
      const height = Math.max(44,Math.min(190,upward ? above : below));
      menu.style.maxHeight = height + 'px';
      menu.style.left = Math.max(12,Math.min(rect.right - width,innerWidth - width - 12)) + 'px';
      menu.style.top = (upward ? rect.top - Math.min(height,menu.scrollHeight) - 5 : rect.bottom + 5) + 'px';
      (menu.querySelector('[aria-selected="true"]:not(:disabled)') || menu.querySelector('button:not(:disabled)'))?.focus({preventScroll:true});
    }
    colorIndex(product) {
      return (product.options || []).findIndex(o => /^(farbe|colou?r)$/i.test(typeof o === 'string' ? o : o.name));
    }
    variantImage(variant) {
      return typeof variant.featured_image === 'string' ? variant.featured_image : variant.featured_image?.src;
    }
    swatch(product, variant) {
      const value = variant.options?.[this.colorIndex(product)] || variant['option' + (this.colorIndex(product) + 1)] || variant.title;
      const colors = {gold:'#d4b453',silber:'#c4c9ce',silver:'#c4c9ce',schwarz:'#25252a',black:'#25252a',blau:'#3674c4',blue:'#3674c4',rosegold:'#d6a18b',roségold:'#d6a18b',weiß:'#fff',weiss:'#fff',white:'#fff',rot:'#c83b4a',red:'#c83b4a',grün:'#458460',green:'#458460',pink:'#df72a5',lila:'#8e62ac',purple:'#8e62ac',rainbow:'linear-gradient(135deg,#d6ad45,#6aa5ae,#9256ab)',bunt:'linear-gradient(135deg,#d6ad45,#6aa5ae,#9256ab)'};
      const dot = node('span','ts-rec-swatch');
      dot.setAttribute('aria-hidden','true');
      const color = colors[String(value).toLowerCase().replace(/[-\s]/g,'')];
      if (color) dot.style.background = color;
      else if (this.variantImage(variant)) {
        const image = node('img',''); image.src = this.variantImage(variant); image.alt = ''; dot.append(image);
      } else dot.classList.add('ts-rec-swatch-unknown');
      return dot;
    }
    async add(variant) {
      if (mutationPending) return;
      mutationPending = true;
      const page = document.querySelector('[data-ts-page-cart]');
      const drawer = document.getElementById('cart');
      const section = page?.dataset.section;
      const sections = [...new Set(['side-cart',section].filter(Boolean))];
      const controls = [...document.querySelectorAll('.ts-rec button,.ts-rec select,[name="checkout"],[data-ts-quantity],[data-ts-qty-step],[data-ts-step]')].map(el => [el,el.disabled]);
      controls.forEach(([el]) => { el.disabled = true; });
      this.setAttribute('aria-busy','true');
      try {
        const response = await request(rootUrl() + 'cart/add.js',{method:'POST',headers:{'Content-Type':'application/json','Accept':'application/json'},body:JSON.stringify({items:[{id:Number(variant.id),quantity:1}],sections,sections_url:location.pathname})});
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(typeof result.description === 'string' ? result.description : this.dataset.error);
        let rendered = result.sections || {};
        if (sections.some(key => !rendered[key])) {
          const url = new URL(location.href); url.searchParams.set('sections',sections.join(','));
          const refresh = await request(url);
          if (!refresh.ok) throw new Error(this.dataset.error);
          rendered = await refresh.json();
        }
        if (sections.some(key => !rendered[key])) throw new Error(this.dataset.error);
        if (section && window.taffstyleCartPage?.render) window.taffstyleCartPage.render(rendered[section]);
        if (window.ajaxCart?.load && drawer) window.ajaxCart.load({sections:rendered},drawer.classList.contains('toggle'));
        document.querySelector('[data-ts-page-status]')?.setAttribute('aria-live','polite');
      } catch (error) {
        this.status.textContent = error.message || this.dataset.error;
      } finally {
        controls.forEach(([el,disabled]) => { if (el.isConnected) el.disabled = disabled; });
        this.removeAttribute('aria-busy');
        mutationPending = false;
      }
    }
  }
  customElements.define('taffstyle-cart-recommendations',CartRecommendations);
})();



